import { motion } from "framer-motion";

const metrics = [
  {
    abbr: "DA",
    name: "Domain Authority",
    body: "A logarithmic 100-point scale predicting how well an entire root domain can rank. Every 10 points up is exponentially harder to earn — DA 60+ is elite territory.",
    gauge: 82,
    tip: "Use DA to shortlist link prospects fast.",
    testid: "metric-card-da",
    color: "#D4FF00",
  },
  {
    abbr: "PA",
    name: "Page Authority",
    body: "The same predictive model, scoped to a single URL. A page on a weaker domain can still outrank giants if its own link profile is strong.",
    gauge: 64,
    tip: "Compare PA when evaluating specific pages, not whole sites.",
    testid: "metric-card-pa",
    color: "#00F0FF",
  },
  {
    abbr: "SS",
    name: "Spam Score",
    body: "The percentage of sites with similar on-page and link features that MOZ found penalized or banned by Google. Under 5% is healthy; above 30% is a red flag.",
    gauge: 12,
    tip: "Filter out high-spam domains before any outreach campaign.",
    testid: "metric-card-spam",
    color: "#F87171",
  },
];

export const Metrics = () => (
  <section id="metrics" data-testid="metrics-explainer-section" className="py-24 sm:py-32 border-t border-[#263248] relative">
    <div className="noise-overlay" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl"
      >
        <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-[#D4FF00] mb-4">// Know Your Numbers</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F3F4F6]">
          What the metrics actually mean
        </h2>
      </motion.div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
        {metrics.map((m, i) => (
          <motion.div
            key={m.abbr}
            data-testid={m.testid}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: i * 0.12 }}
            className="border border-[#263248] bg-[#121824] rounded-xl p-7 hover:border-[#D4FF00]/40 hover:bg-[#1A2234] transition-colors"
          >
            <div className="flex items-center justify-between">
              <span className="font-display text-3xl font-extrabold" style={{ color: m.color }}>{m.abbr}</span>
              <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">0 – 100</span>
            </div>
            <h3 className="mt-4 font-display text-xl font-bold text-[#F3F4F6]">{m.name}</h3>
            <p className="mt-3 text-sm text-[#9CA3AF] leading-relaxed">{m.body}</p>
            <div className="mt-6 h-1.5 rounded-full bg-[#0B0F17] overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${m.gauge}%` }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 0.2 + i * 0.15, ease: "easeOut" }}
                className="h-full rounded-full"
                style={{ background: m.color }}
              />
            </div>
            <p className="mt-4 text-xs text-[#6B7280] italic">{m.tip}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
